import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Info, ShieldCheck } from "lucide-react";

const AUTHORITY_KEY = { NET_AUTHORITATIVE: "taxc.netAuthoritative", GROSS_AUTHORITATIVE: "taxc.grossAuthoritative" };

/** Steuerfall-Karte im Auftragsdialog: Vorschlag, angewendeter Fall, Satz, Grund, Bestätigung.
 *  Der Vorschlag ist beratend — steuerfrei wird erst nach ausdrücklicher Bestätigung angewendet. */
export const TaxCaseCard = ({ organizationId, channel, orderDate, value, onChange }) => {
  const { t, fmtDate } = useI18n();
  const [sug, setSug] = useState(null);

  useEffect(() => {
    const params = { channel };
    if (organizationId) params.organization_id = organizationId;
    if (orderDate) params.on = orderDate;
    api.get("/tax/suggest-case", { params })
      .then((r) => setSug(r.data))
      .catch(() => setSug(null));
  }, [organizationId, channel, orderDate]);

  if (!sug) return null;
  const confirmed = !!value?.tax_exemption_confirmed;
  const applied = sug.requires_confirmation && !confirmed ? sug.applied_without_confirmation : sug.suggested_code;
  const appliedRate = applied === sug.suggested_code ? sug.suggested_rate : sug.domestic_rate;

  return (
    <div className="col-span-2 rounded-md border border-border bg-muted/30 p-3 space-y-2" data-testid="tax-case-card">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        <Label>{t("taxc.section")}</Label>
      </div>
      <div className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div className="flex min-w-0 justify-between gap-3">
          <span className="text-muted-foreground shrink-0">{t("taxc.suggested")}</span>
          <span className="min-w-0 text-right font-medium [hyphens:auto] break-words" data-testid="tax-case-suggested">{t(`tax.code.${sug.suggested_code}`, sug.suggested_code)}</span>
        </div>
        <div className="flex min-w-0 justify-between gap-3">
          <span className="text-muted-foreground shrink-0">{t("taxc.applied")}</span>
          <span className="min-w-0 text-right font-medium [hyphens:auto] break-words" data-testid="tax-case-applied">{t(`tax.code.${applied}`, applied)}</span>
        </div>
        <div className="flex min-w-0 justify-between gap-3">
          <span className="text-muted-foreground shrink-0">{t("tax.rate")}</span>
          <span className="font-mono" data-testid="tax-case-rate">{appliedRate == null ? "—" : `${appliedRate} %`}</span>
        </div>
        <div className="flex min-w-0 justify-between gap-3">
          <span className="text-muted-foreground shrink-0">{t("taxc.customerCountry")}</span>
          <span className="font-mono" data-testid="tax-case-country">{sug.customer_country || "—"}</span>
        </div>
        <div className="flex min-w-0 justify-between gap-3 sm:col-span-2">
          <span className="text-muted-foreground shrink-0">{t("taxc.reason")}</span>
          <span className="min-w-0 text-right [hyphens:auto] break-words">{t(`taxc.hint.${sug.reason}`, sug.reason)}</span>
        </div>
      </div>
      <p className="flex gap-1.5 text-xs text-muted-foreground"><Info className="h-3.5 w-3.5 shrink-0" /> {t("taxc.advisory")}</p>
      {!sug.customer_country && <p className="text-xs text-chart-4" data-testid="tax-case-country-missing">{t("taxc.countryMissing")}</p>}

      {sug.requires_confirmation && (
        <div className="rounded-md border border-chart-3/40 bg-chart-3/5 p-2.5 space-y-2" data-testid="tax-case-confirm-box">
          <div className="text-sm font-medium">{t("taxc.needsConfirm")}</div>
          <p className="text-xs text-muted-foreground">{t("taxc.needsConfirmHint")}</p>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5" checked={confirmed} data-testid="tax-case-confirm"
              onChange={(e) => onChange({
                tax_reason_code: e.target.checked ? sug.suggested_code : null,
                tax_exemption_confirmed: e.target.checked,
                tax_reason_text: value?.tax_reason_text || "",
                evidence_type: e.target.checked ? (sug.evidence_required || "") : "",
                evidence_reference: value?.evidence_reference || "",
              })} />
            <span>{t("taxc.confirmLabel")}</span>
          </label>
          {confirmed && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="sm:col-span-2">
                <Label className="text-xs">{t("taxc.reasonText")}</Label>
                <Input className="mt-1 h-9" value={value?.tax_reason_text || ""} data-testid="tax-case-reason-text"
                  onChange={(e) => onChange({ ...value, tax_reason_text: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">{t("taxc.evidenceType")}</Label>
                <Input className="mt-1 h-9" value={value?.evidence_type || ""} data-testid="tax-case-evidence-type"
                  onChange={(e) => onChange({ ...value, evidence_type: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">{t("taxc.evidenceRef")}</Label>
                <Input className="mt-1 h-9" value={value?.evidence_reference || ""} data-testid="tax-case-evidence-ref"
                  onChange={(e) => onChange({ ...value, evidence_reference: e.target.value })} />
              </div>
              <p className="sm:col-span-2 text-xs text-muted-foreground">{t("taxc.evidenceHint")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Eingefrorener Steuerfall eines bestehenden Belegs (nur Anzeige, nie neu interpretiert). */
export const TaxContextPanel = ({ order }) => {
  const { t } = useI18n();
  const ctx = order?.tax_context || {};
  const sug = order?.tax_suggestion || null;
  const code = ctx.tax_reason_code;
  return (
    <div className="rounded-md border border-border bg-card p-4 shadow-sm space-y-2" data-testid="tax-context-panel">
      <div className="font-heading font-medium">{t("taxc.section")}</div>
      <div className="space-y-1 text-sm">
        {sug && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t("taxc.suggested")}</span>
            <span data-testid="tax-ctx-suggested">{t(`tax.code.${sug.suggested_code}`, sug.suggested_code)}</span>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">{t("taxc.applied")}</span>
          <span data-testid="tax-ctx-applied">{code ? t(`tax.code.${code}`, code) : t("taxc.taxable")}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">{t("tax.category")}</span>
          <span className="font-mono" data-testid="tax-ctx-category">{ctx.tax_category || "S"}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">{t("taxc.confirmed")}</span>
          <span data-testid="tax-ctx-confirmed">{ctx.tax_exemption_confirmed || ctx.tax_reason_confirmed ? t("taxc.confirmed") : t("taxc.notConfirmed")}</span>
        </div>
        {ctx.tax_reason_text && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t("taxc.reason")}</span>
            <span className="text-right">{ctx.tax_reason_text}</span>
          </div>
        )}
        {ctx.evidence_reference && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t("taxc.evidenceRef")}</span>
            <span className="font-mono text-right">{ctx.evidence_reference}</span>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">{t("tax.priceAuthority")}</span>
          <span data-testid="tax-ctx-authority">{t(AUTHORITY_KEY[ctx.price_authority_code] || (ctx.price_authority === "gross" ? "taxc.grossAuthoritative" : "taxc.netAuthoritative"))}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">{t("common.currency")}</span>
          <span className="font-mono">{ctx.currency || "EUR"}</span>
        </div>
      </div>
    </div>
  );
};

/** Steuer-Vorschau für Variante/Bundle: aktuell wirksam, geplant, Kategorie vs. fester Satz. */
export const TaxPreview = ({ entityType = "variant", entityId }) => {
  const { t, fmtDate } = useI18n();
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!entityId) { setP(null); return; }
    api.get(`/${entityType === "bundle" ? "bundles" : "variants"}/${entityId}/tax-preview`)
      .then((r) => setP(r.data)).catch(() => setP(null));
  }, [entityType, entityId]);
  if (!p) return null;
  return (
    <div className="col-span-2 rounded-md border border-border p-3 space-y-1 text-sm" data-testid="tax-preview">
      <div className="flex items-center gap-2"><Label>{t("taxc.previewTitle")}</Label></div>
      <div className="flex justify-between gap-2">
        <span className="text-muted-foreground">{t("tax.case")}</span>
        <span data-testid="tax-preview-mode">{p.tax_code ? t(`tax.code.${p.tax_code}`, p.tax_code) : t("tax.fixedRate")}</span>
      </div>
      <div className="flex justify-between gap-2">
        <span className="text-muted-foreground">{t("taxc.currentRate")}</span>
        <span className="font-mono" data-testid="tax-preview-current">{p.current_rate == null ? "—" : `${p.current_rate} %`}</span>
      </div>
      <div className="flex justify-between gap-2">
        <span className="text-muted-foreground">{t("taxc.nextRate")}</span>
        <span className="font-mono" data-testid="tax-preview-next">{p.next_rate == null ? t("taxc.noNextRate") : `${p.next_rate} % · ${fmtDate(p.next_valid_from)}`}</span>
      </div>
      <p className="text-xs text-muted-foreground">{p.mode === "category" ? t("taxc.modeCategory") : t("taxc.modeFixed")}</p>
      <p className="text-xs text-muted-foreground">{t("taxc.previewHint")}</p>
    </div>
  );
};
